import {
    KeyboardArrowDown as ArrowDownwardIcon,
    KeyboardArrowUp as ArrowUpwardIcon,
    UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import { Box, Checkbox, IconButton, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';

import { ColumnWidths, DataTableColumn, SelectionModel, SortDirection, SortModel } from '../types';
import {
    calculateStickyColumnLeft,
    calculateStickyColumnRight,
    hasStickyColumns,
    hasStickyRightColumns,
} from '../utils';
import { ResizeHandle } from './ResizeHandle';

interface DataTableHeaderProps<T = any> {
    columns: DataTableColumn<T>[];
    sortModel: SortModel[];
    onSort: (columnId: string) => void;
    columnWidths: ColumnWidths;
    columnWidthStyles: Record<string, { width?: string; minWidth?: string; flexGrow?: number }>;
    onColumnResize: (columnId: string, width: number) => void;
    selectionModel: SelectionModel;
    onSelectAll: () => void;
    checkboxSelection: boolean;
    disableMultipleSelection: boolean;
    stickyHeader?: boolean;
    dense: boolean;
    rowHeight: string | number;
    collapsible?: boolean;
    collapseAnchorColumn?: string;
    expandedRows?: Set<string | number>;
    onToggleAllRows?: () => void;
    totalRows?: number;
}

const StyledTableCell = styled(TableCell, {
    shouldForwardProp: prop => !['sortable', 'resizable'].includes(prop as string),
})<{
    sortable?: boolean;
    resizable?: boolean;
}>(({ theme, sortable, resizable }) => ({
    position: 'relative', // Essential for absolutely positioned resize handle
    userSelect: 'none',
    cursor: sortable ? 'pointer' : 'default',
    padding: resizable ? '12px 8px 12px 16px' : '12px 16px',
    background: theme.palette.tableHead.main,
    overflow: 'visible', // Allow resize handle to be visible outside cell bounds
    listStyle: 'none', // Remove any list styling that might cause dots
    '&::before, &::after': {
        display: 'none', // Remove any pseudo-elements that might create dots
    },
    '&:hover': {
        backgroundColor: sortable ? theme.palette.primaryPurple30.main : 'inherit',
    },
    '&.MuiTableCell-head': {
        fontWeight: 600,
        backgroundColor: theme.palette.tableHead.main,
        borderBottom: `2px solid ${theme.palette.border.main}`,
    },
}));

const SortContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
});

const SortIconContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    minWidth: 20,
});

const getSortDirection = (columnId: string, sortModel: SortModel[]): SortDirection => {
    const sort = sortModel.find(s => s.field === columnId);
    return sort ? sort.sort : null;
};

const getSortIcon = (direction: SortDirection) => {
    switch (direction) {
        case 'asc':
            return <ArrowUpwardIcon fontSize="small" />;
        case 'desc':
            return <ArrowDownwardIcon fontSize="small" />;
        default:
            return <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.5 }} />;
    }
};

export const DataTableHeader = <T extends Record<string, any> = any>({
    columns,
    sortModel,
    onSort,
    columnWidths,
    columnWidthStyles,
    onColumnResize,
    selectionModel,
    onSelectAll,
    checkboxSelection,
    disableMultipleSelection,
    stickyHeader = true,
    dense,
    rowHeight,
    collapsible,
    collapseAnchorColumn,
    expandedRows,
    onToggleAllRows,
    totalRows = 0,
}: DataTableHeaderProps<T>) => {
    const handleSort = (columnId: string) => {
        onSort(columnId);
    };

    // Determine collapse/expand state
    const getCollapseState = () => {
        if (!collapsible) return 'none';
        if (totalRows === 0) return 'collapsed'; // Show collapsed state even with no rows
        if (!expandedRows) return 'collapsed'; // Default to collapsed if Set is not provided
        const expandedCount = expandedRows.size;
        if (expandedCount === 0) return 'collapsed';
        if (expandedCount === totalRows) return 'expanded';
        return 'mixed';
    };

    const collapseState = getCollapseState();

    const renderCollapseIcon = () => {
        switch (collapseState) {
            case 'expanded':
                return <i className="fi fi-br-caret-down" />;
            case 'collapsed':
                return <i className="fi fi-br-caret-right" />;
            case 'mixed':
                return <i className="fi fi-br-caret-right" />;
            default:
                return null;
        }
    };

    const getCollapseTooltip = () => {
        switch (collapseState) {
            case 'expanded':
                return 'Collapse all rows';
            case 'collapsed':
                return 'Expand all rows';
            case 'mixed':
                return 'Toggle all rows';
            default:
                return '';
        }
    };

    // Check if any column is sticky to determine if checkbox should also be sticky
    const hasStickyCols = hasStickyColumns(columns);
    const hasStickyRightCols = hasStickyRightColumns(columns);

    const renderSortableHeader = (column: DataTableColumn<T>) => {
        const sortDirection = getSortDirection(column.id, sortModel);
        const sortIcon = getSortIcon(sortDirection);
        const isCollapseAnchor = collapsible && column.id === collapseAnchorColumn;

        if (column.renderHeader) {
            return column.renderHeader({
                column,
                sortDirection,
                handleSort: () => handleSort(column.id),
                isResizing: false, // This could be enhanced to track resize state
            });
        }

        // If this is the collapse anchor column, render with collapse toggle
        if (isCollapseAnchor && collapseState !== 'none' && onToggleAllRows) {
            return (
                <SortContainer>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Tooltip title={getCollapseTooltip()}>
                            <IconButton
                                size="small"
                                onClick={e => {
                                    e.stopPropagation();
                                    onToggleAllRows();
                                }}
                                sx={{ p: 0.25 }}>
                                {renderCollapseIcon()}
                            </IconButton>
                        </Tooltip>
                        <Box
                            component="span"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                fontSize: dense ? '0.75rem' : 'inherit',
                            }}>
                            {column.label}
                        </Box>
                    </Box>
                    {column.sortable && (
                        <SortIconContainer>
                            <Tooltip title={`Sort by ${column.label}`}>
                                <IconButton
                                    size="small"
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleSort(column.id);
                                    }}
                                    sx={{
                                        padding: '2px',
                                        color: sortDirection ? 'primary.main' : 'text.secondary',
                                    }}>
                                    {sortIcon}
                                </IconButton>
                            </Tooltip>
                        </SortIconContainer>
                    )}
                </SortContainer>
            );
        }

        // Default sortable header rendering
        return (
            <SortContainer>
                <Box
                    component="span"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        fontSize: dense ? '0.75rem' : 'inherit',
                    }}>
                    {column.label}
                </Box>
                {column.sortable && (
                    <SortIconContainer>
                        <Tooltip title={`Sort by ${column.label}`}>
                            <IconButton
                                size="small"
                                onClick={e => {
                                    e.stopPropagation();
                                    handleSort(column.id);
                                }}
                                sx={{
                                    padding: '2px',
                                    color: sortDirection ? 'primary.main' : 'text.secondary',
                                }}>
                                {sortIcon}
                            </IconButton>
                        </Tooltip>
                    </SortIconContainer>
                )}
            </SortContainer>
        );
    };

    return (
        <TableHead
            sx={{
                backgroundColor: 'transparent',
                ...(stickyHeader && {
                    position: 'sticky',
                    top: 0,
                    zIndex: 102,
                }),
            }}>
            <TableRow>
                {checkboxSelection && (
                    <StyledTableCell
                        style={{
                            textOverflow: 'unset',
                            width: 48, // Fixed width for checkbox column
                            flex: '0 0 48px', // Prevent checkbox column from resizing with other columns
                        }}
                        sx={{
                            position: hasStickyCols ? 'sticky' : stickyHeader ? 'sticky' : 'relative',
                            left: hasStickyCols ? 0 : 'auto',
                            zIndex: hasStickyCols ? 103 : stickyHeader ? 102 : 'auto', // Higher z-index than sticky columns
                            top: stickyHeader ? 0 : 'auto',
                            backgroundColor: 'none',
                        }}>
                        {!disableMultipleSelection && (
                            <Checkbox
                                indeterminate={selectionModel.isSomeSelected && !selectionModel.isAllSelected}
                                checked={selectionModel.isAllSelected}
                                onChange={onSelectAll}
                                color="primary"
                                size="small"
                            />
                        )}
                    </StyledTableCell>
                )}
                {columns.map((column, index) => {
                    const resizedWidth = columnWidths[column.id];
                    const columnStyle = columnWidthStyles[column.id];
                    // Calculate left position for left-sticky columns
                    const stickyLeft = calculateStickyColumnLeft(
                        column,
                        columns,
                        index,
                        columnWidths,
                        checkboxSelection,
                        dense,
                    );
                    // Calculate right position for right-sticky columns
                    const stickyRight = calculateStickyColumnRight(column, columns, index, columnWidths);
                    const isSticky = column.sticky || column.stickyRight;

                    return (
                        <StyledTableCell
                            key={column.id}
                            align={column.align || 'left'}
                            sortable={column.sortable}
                            resizable={column.resizable}
                            onClick={e => {
                                // Don't trigger sort if clicking on resize handle area
                                const target = e.target as HTMLElement;
                                const isResizeHandle = target.closest('[data-resize-handle]') !== null;

                                if (column.sortable && !isResizeHandle) {
                                    handleSort(column.id);
                                }
                            }}
                            sx={{
                                // Apply the calculated width styles
                                ...columnStyle,
                                // Override with any explicit maximum constraints
                                maxWidth: column.maxWidth,
                                position: isSticky ? 'sticky' : stickyHeader ? 'sticky' : 'relative',
                                left: column.sticky ? stickyLeft : column.stickyRight ? 0 : 'auto',
                                right: column.stickyRight ? `0` : 'auto',
                                zIndex: isSticky ? 103 : stickyHeader ? '101 !important' : 'auto',
                                top: stickyHeader ? 0 : 'auto',
                                backgroundColor: 'none',
                                boxSizing: 'border-box',
                                height: rowHeight,
                                ...(column.stickyRight && {
                                    boxShadow: '-2px 0 4px rgba(0,0,0,0.08)',
                                }),
                            }}>
                            {renderSortableHeader(column)}

                            {column.resizable === true && (
                                <ResizeHandle
                                    onResize={width => onColumnResize(column.id, width)}
                                    initialWidth={
                                        resizedWidth ||
                                        (typeof column.width === 'number'
                                            ? column.width
                                            : typeof column.minWidth === 'number'
                                            ? column.minWidth
                                            : 150)
                                    }
                                />
                            )}
                        </StyledTableCell>
                    );
                })}
            </TableRow>
        </TableHead>
    );
};

export default DataTableHeader;
